import { ChangeDetectionStrategy, ChangeDetectorRef, Component, EventEmitter, Input, OnChanges, Output, SimpleChanges, inject } from '@angular/core'
import { MessageService } from 'primeng/api'

import {
  ACTION_LABELS,
  PERSON_TYPE_LABELS,
  STATUS_UI,
  effectiveStatus,
  latestFound,
  preventionSummary,
  queryButtonState,
  queryResultUi,
  reportTotals,
} from '@modules/private/credit-history/credit-history.labels'
import { AuthorizationStatus, CreditAuthorization, CreditHistoryQuery, CreditHistoryView, ExpedienteAuthorizationFile } from '@data/interfaces/credit-history.interface'
import { ApiCreditHistoryService, AuthorizationFile, ExpedienteTarget } from '@data/services/modules/credit-history/api-credit-history.service'
import { AuthService } from '@data/services/modules/auth/auth.service'

//* El mismo tope que la pestaña Documentos para un PDF del expediente.
const MAX_FILE_BYTES = 100 * 1024 * 1024

/**
 * Pestaña «Buró y Círculo» de la ficha del cliente (plan v4.2 §9): el formato autógrafo con el que el cliente autoriza
 * consultar su historial crediticio, su validación por Legal (E5a) y las consultas a Círculo con su reporte (E5b). La
 * ven Admin, Operaciones, Finanzas, Legal y el Seller (`credit-history.view`), que es quien pide el formato al cliente
 * (decisión del 19-oct). Subir, mandar o reemplazar un formato lo puede cualquiera de ellos; aprobarlo y consultar a
 * Círculo, solo Legal.
 */
@Component({
  selector: 'app-customer-credit-history',
  templateUrl: './customer-credit-history.component.html',
  styleUrls: ['./customer-credit-history.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class CustomerCreditHistoryComponent implements OnChanges {
  /** El cliente de la ficha, con `docs` y `documentType` para subir a su expediente. */
  @Input() client: any = null
  /** Se subió un archivo al expediente: la ficha recarga para que la pestaña Documentos lo vea. */
  @Output() changed = new EventEmitter<void>()

  view: CreditHistoryView | null = null
  loading = false
  loadError: string | null = null
  busy = false
  preview: AuthorizationFile | null = null
  queryDialogVisible = false
  //* Tras un timeout no se sabe si Círculo cobró: consultar otra vez exige marcar que se acepta un posible segundo cargo.
  acknowledgeCharge = false

  readonly actionLabels = ACTION_LABELS
  readonly personTypeLabels = PERSON_TYPE_LABELS
  readonly queryResultUi = queryResultUi
  readonly preventionSummary = preventionSummary
  readonly canValidate: boolean
  readonly canQuery: boolean

  private readonly api = inject(ApiCreditHistoryService)
  private readonly messages = inject(MessageService)
  private readonly cd = inject(ChangeDetectorRef)
  private loadedFor: string | null = null

  constructor() {
    const auth = inject(AuthService)
    this.canValidate = auth.hasModulePermission('credit-history', 'validate')
    this.canQuery = auth.hasModulePermission('credit-history', 'query')
  }

  ngOnChanges(changes: SimpleChanges): void {
    const id = this.client?._id ? String(this.client._id) : null
    if (changes['client'] && id && id !== this.loadedFor) this.load()
  }

  /** La más reciente: la vigente si la hay; si no, la última (pendiente, rechazada o vencida). */
  get latest(): CreditAuthorization | null {
    return this.view?.current ?? this.view?.authorizations[0] ?? null
  }

  get pending(): CreditAuthorization | null {
    return this.view?.authorizations.find((item) => item.status === 'pending') ?? null
  }

  /** El botón «Consultar historial»: oculto, en curso, deshabilitado (con motivos), reutilizar, con advertencia o consultar. */
  get queryButton() {
    return this.view ? queryButtonState(this.view, this.canQuery) : { state: 'hidden' as const, reasons: [] }
  }

  /** La consulta cuyo reporte se muestra: la encontrada más reciente. */
  get report(): CreditHistoryQuery | null {
    return this.view ? latestFound(this.view.queries) : null
  }

  get reportTotals() {
    const summary = this.report?.summary
    return summary ? reportTotals(summary.financialAccounts, summary.commercialAccounts) : null
  }

  openQuery(): void {
    if (this.busy) return
    this.acknowledgeCharge = false
    this.queryDialogVisible = true
  }

  confirmQuery(): void {
    const state = this.queryButton.state
    if (!this.view || this.busy || state === 'hidden' || state === 'disabled' || state === 'in_progress') return
    if (state === 'with_warning' && !this.acknowledgeCharge) return
    this.busy = true
    this.api.query(this.view.client.id, state === 'with_warning').subscribe((response) => {
      this.busy = false
      this.queryDialogVisible = false
      const query = response.data
      if (response.error || !query) this.toast('error', 'No se consultó', response.msg)
      else if (query.status === 'failed') this.toast('error', 'Círculo no respondió con un reporte', query.errorMessage ?? 'La consulta falló.')
      else if (query.status === 'reused') this.toast('success', 'Consulta reutilizada', 'Se usó la consulta reciente, sin costo.')
      else if (query.outcome === 'found') this.toast('success', 'Historial consultado', 'El reporte ya alimenta la calculadora de riesgo.')
      else this.toast('warn', 'Sin registros en Círculo', 'Círculo no tiene historial de este cliente.')
      this.load()
      this.cd.markForCheck()
    })
  }

  stateOf(authorization: CreditAuthorization): AuthorizationStatus {
    return effectiveStatus(authorization)
  }

  statusOf(authorization: CreditAuthorization) {
    return STATUS_UI[effectiveStatus(authorization)]
  }

  canSend(file: ExpedienteAuthorizationFile): boolean {
    return !file.sent && file.reviewStatus !== 'rejected'
  }

  load(): void {
    const id = this.client?._id ? String(this.client._id) : null
    if (!id) return
    this.loadedFor = id
    this.loading = true
    this.loadError = null
    this.api.forClient(id).subscribe((response) => {
      this.loading = false
      if (response.error) this.loadError = response.status === 403 ? 'No tienes permiso para ver el historial crediticio.' : response.msg
      else this.view = response.data
      this.cd.markForCheck()
    })
  }

  onFilePicked(event: Event): void {
    const input = event.target as HTMLInputElement
    const file = input.files?.[0]
    input.value = ''
    if (!file) return
    if (file.type !== 'application/pdf' && !file.name.toLowerCase().endsWith('.pdf')) return this.toast('warn', 'Solo PDF', 'El formato tiene que ser un PDF.')
    if (file.size > MAX_FILE_BYTES) return this.toast('warn', 'Archivo muy grande', 'El PDF no puede pasar de 100 MB.')
    const target = this.target()
    if (!target) return this.toast('error', 'Sin expediente', 'El cliente no tiene expediente de documentos para guardar el formato.')
    this.busy = true
    this.api.uploadAndSend(target, file).subscribe((response) => this.afterSend(response, true))
  }

  send(file: ExpedienteAuthorizationFile): void {
    if (!this.view || this.busy) return
    this.busy = true
    this.api.sendToValidation(this.view.client.id, file.url).subscribe((response) => this.afterSend(response, false))
  }

  openDocument(authorization: CreditAuthorization): void {
    if (this.busy) return
    this.busy = true
    this.api.document(authorization.id).subscribe((response) => {
      this.busy = false
      if (response.error || !response.data) this.toast('error', 'No se pudo abrir el PDF', response.msg)
      else this.preview = response.data
      this.cd.markForCheck()
    })
  }

  closePreview(): void {
    this.preview = null
  }

  trackById(_index: number, item: { id?: string; url?: string }): string {
    return item.id ?? item.url ?? String(_index)
  }

  private afterSend(response: { error: boolean; msg: string }, uploaded: boolean): void {
    this.busy = false
    if (response.error) this.toast('error', 'No se mandó a validación', response.msg)
    else this.toast('success', 'Mandado a validación', 'Legal lo verá en su bandeja de validaciones.')
    //* Aunque el envío falle, una subida que sí entró al expediente se tiene que ver en Documentos y en la lista.
    if (uploaded || !response.error) {
      this.load()
      if (uploaded) this.changed.emit()
    }
    this.cd.markForCheck()
  }

  private target(): ExpedienteTarget | null {
    const docs = this.client?.docs
    const docsId = typeof docs === 'string' ? docs : docs?._id
    if (!docsId || !this.client?.documentType) return null
    return { clientId: String(this.client._id), rfc: this.client.rfc ?? '', docsId: String(docsId), documentType: this.client.documentType }
  }

  private toast(severity: 'success' | 'warn' | 'error', summary: string, detail: string): void {
    this.messages.add({ key: 'credit-history', severity, summary, detail, life: 5000 })
  }
}
